import { useStudioStream } from "@/data/studio-react/useStudioStream";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import { Timestamp, updateDoc } from "firebase/firestore";
import { Form, Formik, useFormikContext } from "formik";
import { Fragment } from "react";
import { Address, AddressWrapper, Customer } from "types";
import * as Yup from "yup";
import { Button, Spinner } from "../Buttons";
import { Client } from "../clients/ClientsDialog";
import AddressField from "../clients/fields/AddressField";

import { autoId } from "@/data/ids";
import { useFirestore } from "@/data/studio-react/firebase/useFirestore";
import { doc } from "firebase/firestore";
import { toast } from "sonner";

interface AddressEditorModalProps {
  open: boolean;
  onClose: (addressId?: string | null, didCreateOrUpdate?: boolean) => void;
  customerId: string | null;
  address: AddressWrapper | null;
  marketId: string | null;
  addAddress?: boolean; // if true, adds into the otherAddresses
}

const AddressSchema = Yup.object().shape({
  country: Yup.string().required("Country is required"),
});
export default function AddressEditorModal({
  open,
  onClose,
  customerId,
  address,
  marketId,
  addAddress = false,
}: AddressEditorModalProps) {
  const market = useStudioStream("session:market");
  let firestore = useFirestore();

  const defaultValues: Client = {
    displayName: "",
    address1: address?.address?.address1 || "",
    address2: address?.address?.address2 || "",
    city: address?.address?.city || "",
    zip: address?.address?.zip || "",
    country: address?.address?.country || "GB-ENG",
    province: address?.address?.province || "",
    company: address?.address?.company || "",
    coordinates: address?.address?.coordinates || undefined,
  };

  const onSubmit = async (values: Client) => {
    // If the address is not empty add it to the customer
    try {
      let address: Address = {
        id: autoId(),

        address1: values.address1 || "",
        address2: values.address2 || "",
        city: values.city || "",
        zip: values.zip || "",
        country: values.country || "",
        province: values.province || "",
        company: values.company || "",
      };

      if (values.coordinates) {
        address.coordinates = values.coordinates;
      }

      // let customerId = await createCustomer(customer);
      if (customerId && marketId) {
        const customerRef = doc(
          firestore,
          `markets/${marketId}/customers/${customerId}`
        );

        let update: Partial<Customer> = {
          updatedAt: new Date(),
        };

        if (addAddress === false) {
          // main address
          update.address = {
            address: address,
            createdAt: Timestamp.now(),
          };
        } else {
          //@ts-ignore
          update[`otherAddresses.${address.id}`] = {
            address: address,
            createdAt: Timestamp.now(),
          };
        }

        await updateDoc(customerRef, update);
        onClose(address.id, addAddress);
      }
    } catch (error) {
      toast.error("Error updating address");
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[110]"
        onClose={() => onClose(null, false)}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <Formik
                  initialValues={defaultValues}
                  onSubmit={onSubmit}
                  validationSchema={AddressSchema}
                >
                  <Form>
                    <div className="px-4 pt-5 sm:p-6">
                      <div className="flex items-start justify-between">
                        <DialogTitle
                          as="h3"
                          className="inline-flex items-center gap-x-1.5 text-xl font-bold"
                        >
                          {address?.address ? "Edit" : "Add"} Address
                        </DialogTitle>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="rounded-full bg-martEye-500 p-1 text-martEye-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-martEye-400 hover:bg-martEye-400"
                            onClick={() => onClose(null, false)}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex flex-wrap gap-2">
                          <AddressField
                            name="address"
                            label="Search Address"
                            defaultCountry={market?.countryCode || "GB-ENG"}
                            marketId={market?.id}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="sticky bottom-0 bg-grey-100 -mx-6 -mb-6 px-12 pb-6 pt-4 mt-4 rounded-b-lg">
                      <SubmitButton />
                    </div>
                  </Form>
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

const SubmitButton = () => {
  const { submitForm, isValid, isSubmitting } = useFormikContext();

  const handleSubmit = () => {
    submitForm();
  };

  let isDisabled = !isValid || isSubmitting;

  return (
    <Button
      type="button"
      onClick={isValid ? handleSubmit : null}
      isDisabled={isDisabled}
      isLoading={isSubmitting}
      className={classNames(
        "bg-martEye-400 text-martEye-100 disabled:opacity-50"
      )}
    >
      Update Address
      {isSubmitting && <Spinner />}
    </Button>
  );
};
